import { Controller } from "@hotwired/stimulus"

// Animates inline text from one value to the next with a slot-machine style roll.
// Intended for short numeric strings such as currency amounts. Other text is allowed,
// but each character gets one fixed-width slot while the animation runs.
export default class extends Controller {
  static values = {
    duration: { type: Number, default: 520 },
    stagger: { type: Number, default: 28 }
  }

  connect() {
    this.currentText = this.element.textContent.trim()
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.stabilizeInlineBox()
    this.finish(this.currentText)
  }

  setValue(nextText) {
    const next = String(nextText)
    const previous = this.currentText || this.element.textContent.trim()

    if (next === previous) return
    if (this.reducedMotion) {
      this.finish(next)
      return
    }

    clearTimeout(this.finishTimer)
    const specs = this.slotSpecs(previous, next)
    this.element.setAttribute("aria-label", next)
    this.element.textContent = ""

    const direction = this.direction(previous, next)
    this.element.appendChild(this.reservedSlots(specs))

    const animationLayer = document.createElement("span")
    animationLayer.setAttribute("aria-hidden", "true")
    this.applyOverlayLayerStyles(animationLayer)
    specs.forEach((spec, index) => {
      animationLayer.appendChild(this.characterSlot(spec, index, direction))
    })
    this.element.appendChild(animationLayer)

    const totalDuration = this.durationValue + (specs.length * this.staggerValue)
    this.finishTimer = setTimeout(() => this.completeAnimation(next), totalDuration + 40)
  }

  disconnect() {
    clearTimeout(this.finishTimer)
  }

  finish(text, specs = this.slotSpecs(text)) {
    this.currentText = text
    this.element.setAttribute("aria-label", text)
    this.element.textContent = ""
    this.element.append(this.reservedSlots(specs), this.staticLayer(text, specs))
  }

  completeAnimation(text) {
    this.currentText = text
    this.element.setAttribute("aria-label", text)
  }

  slotSpecs(previous, next = previous) {
    const length = Math.max(previous.length, next.length)
    const previousChars = previous.padStart(length, " ").split("")
    const nextChars = next.padStart(length, " ").split("")

    return previousChars.map((previousChar, index) => {
      const nextChar = nextChars[index]
      return {
        previousChar,
        nextChar,
        width: Math.ceil(Math.max(this.characterWidth(previousChar), this.characterWidth(nextChar)))
      }
    })
  }

  direction(previous, next) {
    const previousNumber = this.numericValue(previous)
    const nextNumber = this.numericValue(next)

    return nextNumber >= previousNumber ? 1 : -1
  }

  numericValue(text) {
    return Number(String(text).replace(/[^\d.-]/g, "")) || 0
  }

  stabilizeInlineBox() {
    this.element.style.display = "inline-grid"
    this.element.style.position = "relative"
    this.element.style.overflow = "hidden"
    this.element.style.height = "1em"
    this.element.style.lineHeight = "1em"
    this.element.style.verticalAlign = "-0.05em"
    this.element.style.whiteSpace = "nowrap"
  }

  reservedSlots(specs) {
    const span = document.createElement("span")
    span.setAttribute("aria-hidden", "true")
    span.style.gridArea = "1 / 1"
    span.style.display = "inline-flex"
    span.style.visibility = "hidden"
    span.style.whiteSpace = "nowrap"
    specs.forEach((spec) => span.appendChild(this.staticCharacter(" ", spec.width)))

    return span
  }

  staticLayer(text, specs) {
    const chars = text.padStart(specs.length, " ").split("")
    const span = document.createElement("span")
    span.setAttribute("aria-hidden", "true")
    this.applyOverlayLayerStyles(span)
    chars.forEach((char, index) => span.appendChild(this.staticCharacter(char, specs[index].width)))

    return span
  }

  applyOverlayLayerStyles(layer) {
    layer.style.gridArea = "1 / 1"
    layer.style.position = "absolute"
    layer.style.inset = "0"
    layer.style.display = "inline-flex"
    layer.style.whiteSpace = "nowrap"
  }

  characterSlot(spec, index, direction) {
    const { previousChar, nextChar, width } = spec
    if (previousChar === nextChar) return this.staticCharacter(nextChar)

    const slot = document.createElement("span")
    const delay = `${index * this.staggerValue}ms`
    slot.style.position = "relative"
    slot.style.display = "inline-block"
    slot.style.flex = `0 0 ${width}px`
    slot.style.width = `${width}px`
    slot.style.overflow = "hidden"
    slot.style.verticalAlign = "top"
    slot.style.height = "1em"
    slot.style.lineHeight = "1em"

    const outgoing = this.animatedCharacter(previousChar === " " ? "" : previousChar, delay)
    const incoming = this.animatedCharacter(nextChar === " " ? "" : nextChar, delay)
    incoming.style.transform = `translateY(${-direction * 115}%)`

    slot.append(outgoing, incoming)

    requestAnimationFrame(() => {
      outgoing.style.transform = `translateY(${direction * 115}%)`
      incoming.style.transform = "translateY(0)"
    })

    return slot
  }

  staticCharacter(char, width = this.characterWidth(char)) {
    const span = document.createElement("span")
    span.textContent = char === " " ? "" : char
    span.style.display = "inline-block"
    span.style.flex = `0 0 ${width}px`
    span.style.width = `${width}px`
    span.style.textAlign = "center"
    return span
  }

  animatedCharacter(char, delay) {
    const span = document.createElement("span")
    span.textContent = char
    span.style.position = "absolute"
    span.style.inset = "0"
    span.style.textAlign = "center"
    span.style.transition = `transform ${this.durationValue}ms cubic-bezier(0.22, 1, 0.36, 1)`
    span.style.transitionDelay = delay
    span.style.willChange = "transform"
    return span
  }

  characterWidth(char) {
    if (char === " ") return 0

    this.characterWidthCache ||= {}
    const cacheKey = `${window.getComputedStyle(this.element).font}::${char}`
    if (this.characterWidthCache[cacheKey] !== undefined) return this.characterWidthCache[cacheKey]

    const span = document.createElement("span")
    span.textContent = char
    span.style.position = "absolute"
    span.style.visibility = "hidden"
    span.style.whiteSpace = "nowrap"
    span.style.font = window.getComputedStyle(this.element).font
    document.body.appendChild(span)
    const width = span.getBoundingClientRect().width
    span.remove()

    this.characterWidthCache[cacheKey] = width

    return width
  }
}
